/*!
 * feedaty-aggregator.js
 *
 * Aggrega lato browser le recensioni Feedaty di tutti i sibling colore di un
 * prodotto (= varianti colore "esplose" come prodotti Shopify separati) e le
 * mostra nello stesso widget Feedaty visibile della PDP.
 *
 * Strategia (no backend, no API OAuth, no clone DOM):
 *   1. Un piccolo monkey-patch inline in layout/theme.liquid, eseguito PRIMA di
 *      feedaty.min.js, intercetta tutte le fetch verso
 *      widget.feedaty.com/v3.0.0/template/<style>,<type>,<variant>,<id>/<merchant>/<sku>.json
 *      e ne salva URL + risposta JSON in window.__feedatyFetchCaptures.
 *
 *   2. Quando Feedaty ha popolato i widget visibili (evento "FeedatyLoaded" o
 *      polling su FeedatyWidget.firstLoad === false), per ogni .feedaty_widget
 *      di tipo product/carouselproduct presente in pagina:
 *        - Recuperiamo la capture che corrisponde al data-sku del widget.
 *        - L'URL Feedaty contiene il SKU come ultimo segmento prima di .json:
 *          ricostruiamo lo stesso URL sostituendo il SKU per ogni sibling.
 *        - Facciamo fetch in parallelo (la patch ricattura anche queste).
 *        - Da ogni risposta leggiamo settings.AvgVote / settings.TotalReviews
 *          (se Feedaty li espone) per il rating sintetico, e parsiamo
 *          l'html del template per estrarre i nodi "review card".
 *
 *   3. Iniezione nel widget visibile:
 *        - Per il rating (Striscia-full-nologo): aggiorniamo testualmente i
 *          numeri (media e count) nel DOM gia popolato da Feedaty, e
 *          sostituiamo l'SVG delle stelle col file del rating aggregato.
 *        - Per il carosello (recensioni-carosello-1): appendiamo le review card
 *          dei sibling dentro lo stesso container delle card del widget visibile
 *          (le card hanno lo stesso markup perche il wrapper type+variant e' lo
 *          stesso, quindi gli stili Feedaty si applicano automaticamente).
 *
 *   4. Modal "Tutte le opinioni" (#feedaty_modal_merch):
 *        Tab bar su #fdt_frameContainer: "Tutti i colori" (lista aggregata con
 *        paginazione MODAL_AGGREGATED_PAGE_SIZE) + un tab per handle colore.
 *        L'iframe punta a URL canonici https://www.feedaty.com/recensioni/{shop}/{handle}?nocookie=...
 *        (il path legacy /feedaty/reviews/ risponde 301). URL preferiti da settings per id
 *        prodotto, mappati su handle via feedaty-siblings-data.
 *
 *   5. Gate visivo: con piu' colori nascondiamo subito il contenuto VISIBILE del
 *      widget (carousel/rating), NON la modale .feedaty_modal_merch (e' figlia
 *      del widget: altrimenti tab e tool restano invisibili anche a modale aperta).
 *
 *   6. Portale modale: all'apertura, OGNI modale prodotto viene spostata su
 *      <body> (portalProductModalsOnOpen), cosi' esce dagli stacking context
 *      del tema (incluso transform:scale su .feedaty_widget). I click su
 *      X/backdrop vengono reinoltrati al widget di origine
 *      (installPortaledModalCloseHandlers) perche' il toggle Feedaty vive li'.
 *      CSS di accompagnamento in feedaty-aggregator-custom.css:
 *      body > #feedaty_modal_merch.feedaty_modal_merch { position:fixed; inset:0; z-index:100000 }
 *
 * Fallback safe: se qualunque step fallisce (siblings vuoti, capture mancante,
 * fetch in errore, settings senza AvgVote, ecc) il widget visibile resta come
 * Feedaty l'ha popolato di default per il singolo prodotto. Niente errori
 * visibili in UI.
 *
 * Tutto e' loggato in console (DEBUG=true) e in window.__feedatyAggregator per
 * tuning rapido.
 */
(function () {
  'use strict';

  var DEBUG = true;
  var FETCH_TIMEOUT_MS = 10000;
  var MAX_FEEDATY_READY_WAIT_MS = 20000;
  var POLL_INTERVAL_MS = 200;
  var CAPTURE_WAIT_MS = 8000;
  /** Recensioni per pagina nel tab "Tutti i colori" (lista aggregata). */
  var MODAL_AGGREGATED_PAGE_SIZE = 10;
  /** Se ready non arriva mai (errore raro), mostriamo comunque i widget. */
  var WIDGET_GATE_FAILSAFE_MS = 90000;
  var WIDGET_GATE_STYLE_ID = 'feedaty-aggregator-widget-gate-style';
  var ATTR_FEEDATY_AGG_READY = 'data-fa-feedaty-aggregator-ready';

  function log() {
    if (!DEBUG) return;
    try {
      var args = Array.prototype.slice.call(arguments);
      args.unshift('[feedaty-aggregator]');
      console.log.apply(console, args);
    } catch (e) {}
  }

  /*
   * Solo i widget di tipo "prodotto" vanno aggregati con i sibling. Il merchant
   * widget non e legato a uno specifico prodotto, quindi non ha senso
   * aggregarlo per famiglia colore (e cloni con merchant generano solo
   * NetworkError in feedaty_merchant.js).
   */
  var PRODUCT_TYPES = ['product', 'carouselproduct'];

  function readSiblingsData() {
    var el = document.getElementById('feedaty-siblings-data');
    if (!el) return null;
    try {
      var data = JSON.parse(el.textContent || el.innerText || '{}');
      if (!data) return null;
      data.currentId = String(data.currentId || '').trim();
      data.currentHandle = String(data.currentHandle || '');
      // Nuovo formato: lista oggetti {id, handle, color, title, isCurrent}.
      // Vecchio formato: solo siblingIds (lista stringhe).
      if (Array.isArray(data.siblings)) {
        data.siblings = data.siblings.map(function (s) {
          return {
            id: String(s && s.id || '').trim(),
            handle: String(s && s.handle || '').trim(),
            color: String(s && s.color || '').trim(),
            title: String(s && s.title || '').trim(),
            image: String(s && s.image || '').trim(),
            isCurrent: !!(s && s.isCurrent)
          };
        });
        if (!Array.isArray(data.siblingIds)) {
          data.siblingIds = data.siblings.map(function (s) { return s.id; });
        }
      }
      if (!Array.isArray(data.siblingIds)) return null;
      data.siblingIds = data.siblingIds.map(function (x) { return String(x).trim(); });
      return data;
    } catch (e) {
      log('JSON parse error', e);
      return null;
    }
  }

  var widgetGateFailsafeTimer = null;
  var widgetGateReadyObserver = null;

  function disconnectFeedatyWidgetGateObserver() {
    if (widgetGateReadyObserver) {
      try {
        widgetGateReadyObserver.disconnect();
      } catch (e) {}
      widgetGateReadyObserver = null;
    }
  }

  function clearFeedatyWidgetGate() {
    try {
      document.documentElement.classList.remove('fa-feedaty-widget-gated');
      document.documentElement.removeAttribute(ATTR_FEEDATY_AGG_READY);
    } catch (e) {}
    disconnectFeedatyWidgetGateObserver();
    if (widgetGateFailsafeTimer) {
      try {
        clearTimeout(widgetGateFailsafeTimer);
      } catch (e2) {}
      widgetGateFailsafeTimer = null;
    }
  }

  function tryRevealFeedatyWidgetsFromReadyAttr() {
    try {
      if (!document.documentElement.classList.contains('fa-feedaty-widget-gated')) return;
      if (document.documentElement.getAttribute(ATTR_FEEDATY_AGG_READY) === '1') {
        if (DEBUG) log('widget gate: readiness segnalata, mostro widget');
        clearFeedatyWidgetGate();
      }
    } catch (e) {}
  }

  /**
   * Dopo che Feedaty e' pronto e partono i fetch: osserva l'attributo su <html>
   * che settiamo a fine aggregazione (dopo doppio rAF). Cosi' non dipendiamo da
   * timer corti per prodotto. Failsafe lungo solo come rete.
   */
  function beginFeedatyWidgetGateReadyObserver() {
    disconnectFeedatyWidgetGateObserver();
    if (widgetGateFailsafeTimer) {
      try {
        clearTimeout(widgetGateFailsafeTimer);
      } catch (e) {}
      widgetGateFailsafeTimer = null;
    }
    widgetGateFailsafeTimer = setTimeout(function () {
      widgetGateFailsafeTimer = null;
      if (DEBUG) log('widget gate: failsafe', WIDGET_GATE_FAILSAFE_MS, 'ms, mostro widget');
      clearFeedatyWidgetGate();
    }, WIDGET_GATE_FAILSAFE_MS);

    try {
      widgetGateReadyObserver = new MutationObserver(function (mutations) {
        for (var i = 0; i < mutations.length; i++) {
          if (mutations[i].type === 'attributes' && mutations[i].attributeName === ATTR_FEEDATY_AGG_READY) {
            tryRevealFeedatyWidgetsFromReadyAttr();
            return;
          }
        }
      });
      widgetGateReadyObserver.observe(document.documentElement, {
        attributes: true,
        attributeFilter: [ATTR_FEEDATY_AGG_READY]
      });
      tryRevealFeedatyWidgetsFromReadyAttr();
    } catch (err) {
      log('widget gate: MutationObserver non disponibile', err);
    }
  }

  /** Dopo paint: aggregazione e populateOneModal sono sync; rAF x2 evita flash pre-tool. */
  function scheduleFeedatyAggregatorReadyMarker() {
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        try {
          document.documentElement.setAttribute(ATTR_FEEDATY_AGG_READY, '1');
        } catch (e) {}
      });
    });
  }

  /**
   * Stessa condizione di start(): metafield siblings con almeno un altro colore.
   * Nasconde solo i figli DIRETTI del widget tranne .feedaty_modal_merch (la modale
   * e' appendChild sul widget: non va nascosta o tab/iframe restano invisibili).
   * La riapertura e' legata a data-fa-feedaty-aggregator-ready (MutationObserver) + failsafe.
   * @param {object|null} prefetchedData - risultato di readSiblingsData() gia' letto in start();
   *   se omesso, rilegge dal DOM (utile solo se #feedaty-siblings-data e' gia' presente).
   */
  function applyFeedatyWidgetGateIfAggregating(prefetchedData) {
    var data = prefetchedData || readSiblingsData();
    if (!data) return false;
    var others = data.siblingIds.filter(function (id) { return id && id !== data.currentId; });
    if (others.length === 0) return false;
    try {
      document.documentElement.classList.add('fa-feedaty-widget-gated');
    } catch (e) {
      return false;
    }
    if (!document.getElementById(WIDGET_GATE_STYLE_ID)) {
      var st = document.createElement('style');
      st.id = WIDGET_GATE_STYLE_ID;
      st.textContent =
        'html.fa-feedaty-widget-gated .feedaty_widget[data-type="carouselproduct"] > *:not(.feedaty_modal_merch),' +
        'html.fa-feedaty-widget-gated .feedaty_widget[data-type="product"] > *:not(.feedaty_modal_merch),' +
        'html.fa-feedaty-widget-gated .feedaty_widget[data-type="carouselproduct" i] > *:not(.feedaty_modal_merch),' +
        'html.fa-feedaty-widget-gated .feedaty_widget[data-type="product" i] > *:not(.feedaty_modal_merch)' +
        '{visibility:hidden!important;opacity:0!important;pointer-events:none!important;}';
      try {
        (document.head || document.documentElement).appendChild(st);
      } catch (e2) {}
    }
    if (DEBUG) log('widget gate: widget nascosti (mostra su ready o failsafe', WIDGET_GATE_FAILSAFE_MS, 'ms)');
    return true;
  }

  function buildProductByIdMap(siblings) {
    var m = {};
    (siblings || []).forEach(function (s) {
      if (s && s.id) m[String(s.id)] = s;
    });
    return m;
  }

  /** Fisher–Yates: ordine casuale in-place (tab "Tutti i colori" in modal). */
  function shuffleArrayInPlace(arr) {
    if (!arr || arr.length < 2) return arr;
    for (var i = arr.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var t = arr[i];
      arr[i] = arr[j];
      arr[j] = t;
    }
    return arr;
  }

  /*
   * Avvolge una card recensione Feedaty con badge prodotto (immagine + titolo in evidenza + colore se diverso).
   * meta: { color, title, handle, image } da feedaty-siblings-data.
   */
  function wrapReviewCardWithOrigin(reviewNode, meta) {
    var wrap = document.createElement('div');
    wrap.className = 'fa-review-origin-wrap';
    if (meta && meta.handle) wrap.setAttribute('data-product-handle', meta.handle);

    var origin = document.createElement('div');
    origin.className = 'fa-review-origin';

    if (meta && meta.image) {
      var img = document.createElement('img');
      img.className = 'fa-review-origin__img';
      img.src = meta.image;
      img.alt = (meta.title || meta.color || '').trim() || 'Prodotto';
      img.width = 56;
      img.height = 56;
      img.loading = 'lazy';
      img.decoding = 'async';
      origin.appendChild(img);
    } else {
      var ph = document.createElement('div');
      ph.className = 'fa-review-origin__img fa-review-origin__img--placeholder';
      ph.setAttribute('aria-hidden', 'true');
      origin.appendChild(ph);
    }

    var textWrap = document.createElement('div');
    textWrap.className = 'fa-review-origin__text';
    var colorLabel = meta && meta.color ? String(meta.color).trim() : '';
    var titleLabel = meta && meta.title ? String(meta.title).trim() : '';
    /* Nome prodotto in evidenza; sottotitolo = colore se diverso dal titolo. */
    if (titleLabel) {
      var linePrimary = document.createElement('div');
      linePrimary.className = 'fa-review-origin__color';
      linePrimary.textContent = titleLabel;
      textWrap.appendChild(linePrimary);
    } else if (colorLabel) {
      var lineColorOnly = document.createElement('div');
      lineColorOnly.className = 'fa-review-origin__color';
      lineColorOnly.textContent = colorLabel;
      textWrap.appendChild(lineColorOnly);
    }
    if (colorLabel && titleLabel && colorLabel !== titleLabel) {
      var lineTitle = document.createElement('div');
      lineTitle.className = 'fa-review-origin__title';
      lineTitle.textContent = colorLabel;
      textWrap.appendChild(lineTitle);
    }
    if (!colorLabel && !titleLabel) {
      var lineFallback = document.createElement('div');
      lineFallback.className = 'fa-review-origin__color';
      lineFallback.textContent = 'Prodotto';
      textWrap.appendChild(lineFallback);
    }
    origin.appendChild(textWrap);

    var cardSlot = document.createElement('div');
    cardSlot.className = 'fa-review-origin__card';
    try {
      cardSlot.appendChild(reviewNode.cloneNode(true));
    } catch (e) {}

    wrap.appendChild(origin);
    wrap.appendChild(cardSlot);
    return wrap;
  }

  function normalizeModalReviewInput(input) {
    if (!input || !input.length) return [];
    if (input[0] && Object.prototype.hasOwnProperty.call(input[0], 'node')) return input;
    return input.map(function (n) {
      return { node: n, meta: null, productId: null };
    });
  }

  function isProductWidget(el) {
    var type = (el.getAttribute('data-type') || '').toLowerCase();
    return PRODUCT_TYPES.indexOf(type) !== -1;
  }

  function findVisibleProductWidgets() {
    return Array.prototype.slice
      .call(document.querySelectorAll('.feedaty_widget'))
      .filter(isProductWidget);
  }

  /*
   * URL Feedaty pattern reale osservato in produzione:
   *
   *   https://widget.feedaty.com/<app_version>/template/<style_ver>,<type>,<variant>,<data-id>/<merchant>/<guiLang>,<filterLang>/<SKU>,<extra>,.json
   *
   * Esempi:
   *   .../template/2021,product,Striscia-full-nologo,selettore2/10217076/it-IT,all/9465476907332,4,.json
   *   .../template/2021,carouselproduct,recensioni-carosello-1,65649fee7ca8e/10217076/it-IT,all/9465476907332,4,.json
   *
   * L'ultimo segmento path prima di .json e' "<SKU>,<extra1>,<extra2>,..." dove
   * lo SKU e' SOLO la prima parte. Quando ricostruiamo per un altro SKU dobbiamo
   * sostituire solo quella, mantenendo il resto invariato (per il merchant
   * widget, dove SKU e' vuoto, lasciamo l'URL inalterato perche non lo
   * aggregheremo comunque).
   */
  function urlForSku(origUrl, newSku) {
    return origUrl.replace(/\/([^\/]+)\.json(\?|$)/, function (_, finalSeg, qs) {
      var parts = finalSeg.split(',');
      parts[0] = encodeURIComponent(newSku);
      return '/' + parts.join(',') + '.json' + qs;
    });
  }

  function getCaptures() {
    return Array.isArray(window.__feedatyFetchCaptures) ? window.__feedatyFetchCaptures : [];
  }

  function escapeForRegex(s) {
    return String(s).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  /*
   * Match capture per SKU: nel path l'ultimo segmento e' "<sku>[,extra,...].json".
   * Il vecchio match "/SKU.json" non funzionava perche dopo lo SKU c'e una
   * virgola (es. "/9465476907332,4,.json"), non un punto.
   */
  function findCaptureForSku(sku) {
    var caps = getCaptures();
    if (!sku) return null;
    var re = new RegExp('/' + escapeForRegex(sku) + '[,.]');
    for (var i = caps.length - 1; i >= 0; i--) {
      if (re.test(caps[i].url)) return caps[i];
    }
    return null;
  }

  /**
   * Stesso SKU compare in piu' fetch (Striscia product + carosello carouselproduct).
   * findCaptureForSku(sku) prendeva l'ultima capture per path: il job carosello
   * poteva leggere il JSON della Striscia -> zero reviewNodes -> modalReviewItems vuoto.
   * Filtriamo per data-type (e data-variant) come nel segmento /template/...,type,variant,...
   */
  function findCaptureForWidget(widget) {
    var sku = widget.getAttribute('data-sku');
    if (!sku) return null;
    var caps = getCaptures();
    var re = new RegExp('/' + escapeForRegex(String(sku).trim()) + '[,.]');
    var type = (widget.getAttribute('data-type') || '').toLowerCase();
    var variant = widget.getAttribute('data-variant') || '';
    var typeNeedle = type ? ',' + type + ',' : null;
    var variantNeedle = variant ? ',' + variant + ',' : null;
    for (var i = caps.length - 1; i >= 0; i--) {
      if (!re.test(caps[i].url)) continue;
      if (typeNeedle && caps[i].url.indexOf(typeNeedle) === -1) continue;
      if (variantNeedle && caps[i].url.indexOf(variantNeedle) === -1) continue;
      return caps[i];
    }
    if (!typeNeedle) return findCaptureForSku(sku);
    return null;
  }

  function waitForCaptureForWidget(widget, timeoutMs) {
    return new Promise(function (resolve) {
      var found = findCaptureForWidget(widget);
      if (found) { resolve(found); return; }
      var start = Date.now();
      var iv = setInterval(function () {
        var c = findCaptureForWidget(widget);
        if (c) { clearInterval(iv); resolve(c); return; }
        if (Date.now() - start > timeoutMs) { clearInterval(iv); resolve(null); }
      }, POLL_INTERVAL_MS);
    });
  }

  function fetchTemplate(url) {
    return new Promise(function (resolve) {
      var timedOut = false;
      var timer = setTimeout(function () {
        timedOut = true;
        resolve({ url: url, error: 'timeout' });
      }, FETCH_TIMEOUT_MS);
      // La fetch passa anche dal monkey-patch, quindi finira' anche in
      // window.__feedatyFetchCaptures (utile per debug). Qui leggiamo
      // direttamente la response perche ci serve subito.
      fetch(url, { credentials: 'omit' })
        .then(function (r) {
          if (!r.ok) throw new Error('HTTP ' + r.status);
          return r.json();
        })
        .then(function (json) {
          if (timedOut) return;
          clearTimeout(timer);
          resolve({ url: url, json: json });
        })
        .catch(function (e) {
          if (timedOut) return;
          clearTimeout(timer);
          resolve({ url: url, error: String(e && e.message || e) });
        });
    });
  }

  /*
   * Estrae i dati di interesse da una risposta Feedaty.
   *
   * Struttura osservata nella min.js (renderWidget):
   *   { "<data-id>": { "html": "<HTML del widget>", "settings": {...} } }
   *
   * Le chiavi dentro `settings` non sono documentate, quindi qui proviamo
   * varianti note del nome (AvgVote/Rating/AverageRating per la media,
   * TotalReviews/ReviewCount/NumReviews per il count, Reviews/ReviewList per
   * l'eventuale array strutturato di review). Per le review estraiamo anche dai
   * nodi HTML, come fallback.
   */
  function pickEntry(json) {
    if (!json) return null;
    if (json.html || json.settings) return json;
    var keys = Object.keys(json);
    for (var i = 0; i < keys.length; i++) {
      var v = json[keys[i]];
      if (v && (v.html || v.settings)) return v;
    }
    return null;
  }

  function num(v) {
    if (v === null || v === undefined) return null;
    var n = parseFloat(String(v).replace(',', '.'));
    return isFinite(n) ? n : null;
  }

  function intOrNull(v) {
    if (v === null || v === undefined) return null;
    var n = parseInt(String(v), 10);
    return isFinite(n) ? n : null;
  }

  function readSettingsStats(settings) {
    if (!settings || typeof settings !== 'object') return { rating: null, count: null };
    var rating =
      num(settings.AvgVote) ||
      num(settings.AverageVote) ||
      num(settings.AverageRating) ||
      num(settings.Rating) ||
      num(settings.Vote);
    var count =
      intOrNull(settings.TotalReviews) ||
      intOrNull(settings.ReviewCount) ||
      intOrNull(settings.NumReviews) ||
      intOrNull(settings.Reviews && settings.Reviews.length);
    return { rating: rating, count: count };
  }

  /*
   * Selettori review per il template carouselproduct/recensioni-carosello-1.
   * Markup osservato in produzione (vedi commento accanto a injectIntoCarousel):
   *   .feedaty_widget > .fdt_widget_wrapper > .fdt_carousel > .fdt_carousel__screen
   *   > a.fdt_reviews > #fdt_block.fdt_rev_block > .fdt_carousel__track
   *   > .fdt_carousel__item (= LA SLIDE = LA CARD review che vogliamo)
   *
   * IMPORTANTE: estraiamo le SLIDE (.fdt_carousel__item), NON i link wrapper
   * .fdt_reviews che contengono un track intero. Selettori vecchi tipo
   * [class*="_review"] erano sbagliati perche matchavano "fdt_reviews".
   */
  var REVIEW_SELECTORS = [
    '[class*="carousel__item"]',
    '[class*="review-item"]',
    '[class*="ReviewItem"]',
    '[class*="recensione"]:not([class*="recensioni-"])',
    '[class*="zopinione_singola"]',
    '[class*="opinione_singola"]',
    '[itemtype*="Review"]',
    '[class*="comment-item"]'
  ].join(', ');

  function parseHtmlToDoc(html) {
    if (!html || typeof html !== 'string') return null;
    try {
      return new DOMParser().parseFromString(html, 'text/html');
    } catch (e) {
      return null;
    }
  }

  /*
   * Heuristica di pulizia: una "review card" e' un nodo MATCH che NON contiene
   * altri nodi MATCH (preferiamo le foglie, non gli antenati). E con un
   * blocco di testo significativo (>= 15 char) per scartare wrapper vuoti o
   * icone svg con classi accidentalmente matching.
   */
  function extractReviewNodesFromHtml(html) {
    var doc = parseHtmlToDoc(html);
    if (!doc) return [];
    var found = doc.body.querySelectorAll(REVIEW_SELECTORS);
    var out = [];
    var seen = new Set ? new Set() : null;
    for (var i = 0; i < found.length; i++) {
      var n = found[i];
      // Preferisci foglie: scarta se questo nodo contiene altri nodi del match.
      var skip = false;
      for (var j = 0; j < found.length; j++) {
        if (j !== i && n.contains(found[j])) { skip = true; break; }
      }
      if (skip) continue;
      var t = (n.textContent || '').trim();
      if (t.length < 15) continue;
      if (seen) {
        if (seen.has(t)) continue;
        seen.add(t);
      }
      out.push(n.cloneNode(true));
    }
    return out;
  }

  /*
   * Per il debug: ritorna info sui nodi candidati a "review" trovati nel doc,
   * cosi vediamo a colpo d'occhio quali classi Feedaty sta usando.
   */
  function debugReviewMatchSummary(html) {
    var doc = parseHtmlToDoc(html);
    if (!doc) return { selectorMatches: 0, sampleClasses: [] };
    var nodes = doc.body.querySelectorAll(REVIEW_SELECTORS);
    var classes = [];
    for (var i = 0; i < nodes.length && classes.length < 6; i++) {
      var c = nodes[i].className;
      if (typeof c === 'string' && c) classes.push(c.slice(0, 120));
    }
    return { selectorMatches: nodes.length, sampleClasses: classes };
  }

  /*
   * Stats prioritarie: il widget Feedaty (almeno Striscia-full-nologo) include
   * sempre nel suo html un blocco <script type="application/ld+json"> con
   * aggregateRating.ratingValue e aggregateRating.reviewCount. E' il dato piu
   * pulito e affidabile, lo usiamo per primo.
   *
   * Esempio (osservato):
   *   {
   *     "@context": "https://schema.org/",
   *     "@type": "Product",
   *     "name": "Cappello ...",
   *     "aggregateRating": { "@type": "AggregateRating", "ratingValue": "5", "reviewCount": "34" }
   *   }
   */
  function extractJsonLdStats(doc) {
    if (!doc) return { rating: null, count: null };
    var scripts = doc.querySelectorAll('script[type="application/ld+json"]');
    for (var i = 0; i < scripts.length; i++) {
      var raw = scripts[i].textContent || '';
      if (!raw) continue;
      var ld;
      try { ld = JSON.parse(raw); } catch (e) { continue; }
      var candidates = [];
      if (Array.isArray(ld)) candidates = ld;
      else if (ld && Array.isArray(ld['@graph'])) candidates = ld['@graph'];
      else candidates = [ld];
      for (var k = 0; k < candidates.length; k++) {
        var ar = candidates[k] && candidates[k].aggregateRating;
        if (!ar) continue;
        var r = num(ar.ratingValue || ar.value);
        var c = intOrNull(ar.reviewCount || ar.ratingCount);
        if (r != null || c != null) return { rating: r, count: c };
      }
    }
    return { rating: null, count: null };
  }

  /*
   * Fallback per quando JSON-LD non e' presente nel template (es. carosello).
   * Selettori Feedaty osservati:
   *   - .fdt_productStriscia-full-nologo_ratx       -> "5,0"
   *   - .fdt_productStriscia-full-nologo_txt_opinions -> "34"
   * In generale tutti i widget product-* usano "_ratx" per il rating e
   * "_txt_opinions" per il count, con prefisso fdt_<type><variant>_.
   */
  function extractStatsFromHtmlNodes(doc) {
    if (!doc) return { rating: null, count: null };
    var rating = null;
    var count = null;
    var prioR = doc.body.querySelectorAll(
      '[itemprop="ratingValue"], [data-rating], [data-vote], ' +
        '[class*="_ratx"]:not([class*="_ratx_star"]):not([class*="_sum"]), ' +
        '[class*="rating-value"], [class*="ratingValue"], ' +
        '.fdt-rating-value, .fdt-vote-value, ' +
        '.zopinione_voto, .zopinionetotale_voto'
    );
    for (var i = 0; i < prioR.length && rating == null; i++) {
      var t = (prioR[i].textContent || prioR[i].getAttribute('content') || '').trim();
      var m = t.match(/(\d(?:[.,]\d{1,2})?)/);
      if (m) {
        var nr = num(m[1]);
        if (nr != null && nr >= 0 && nr <= 5) rating = nr;
      }
    }
    var prioC = doc.body.querySelectorAll(
      '[itemprop="reviewCount"], [itemprop="ratingCount"], [data-count], ' +
        '[class*="_txt_opinions"], ' +
        '[class*="reviews-count"], [class*="reviewsCount"], ' +
        '.fdt-reviews-count, .fdt-total'
    );
    for (var j = 0; j < prioC.length && count == null; j++) {
      var rawC = (prioC[j].textContent || prioC[j].getAttribute('content') || '').trim();
      var mc = rawC.match(/\d+/);
      if (mc) {
        var nc = intOrNull(mc[0]);
        if (nc != null && nc >= 0) count = nc;
      }
    }
    return { rating: rating, count: count };
  }

  function extractEntryStats(entry) {
    if (!entry) return { rating: null, count: null, reviewNodes: [], html: null };
    var doc = parseHtmlToDoc(entry.html);
    var fromLd = extractJsonLdStats(doc);
    var fromSettings = readSettingsStats(entry.settings);
    var fromHtml = extractStatsFromHtmlNodes(doc);
    var rating =
      fromLd.rating != null ? fromLd.rating :
      fromSettings.rating != null ? fromSettings.rating :
      fromHtml.rating;
    var count =
      fromLd.count != null ? fromLd.count :
      fromSettings.count != null ? fromSettings.count :
      fromHtml.count;
    var nodes = extractReviewNodesFromHtml(entry.html);
    return {
      rating: rating, count: count, reviewNodes: nodes,
      html: entry.html, settings: entry.settings,
      _src: { ld: fromLd, settings: fromSettings, html: fromHtml }
    };
  }

  function aggregateStats(perSiblingStats) {
    var weighted = 0;
    var totalCount = 0;
    var totalNodes = [];
    perSiblingStats.forEach(function (s) {
      if (typeof s.rating === 'number' && typeof s.count === 'number' && s.count > 0) {
        weighted += s.rating * s.count;
        totalCount += s.count;
      } else if (typeof s.rating === 'number' && (s.count == null || s.count === 0)) {
        // peso default 1 se conosciamo la media ma non il count
        weighted += s.rating;
        totalCount += 1;
      }
      if (s.reviewNodes && s.reviewNodes.length) {
        totalNodes = totalNodes.concat(s.reviewNodes);
      }
    });
    return {
      rating: totalCount > 0 ? Math.round((weighted / totalCount) * 10) / 10 : null,
      count: totalCount,
      reviewNodes: totalNodes
    };
  }

  /*
   * Caso Striscia: stelle = singola <img> con SVG pre-renderizzato per voto
   * (es. .../stars/onlystars/4.svg; sul CDN esistono anche frazionari, vedi
   * 4,8 renderizzato con stella parziale su altri prodotti). Sostituiamo il
   * filename col rating aggregato provando i candidati dal piu' preciso al
   * piu' grossolano: decimale esatto (4.7 / 4_7 / 4-7), mezzo voto
   * (4.5 / 4_5 / 4-5), intero arrotondato (5). Probe con Image(): se il file
   * non esiste sul CDN si passa al candidato successivo; se nessuno esiste,
   * le stelle restano com'erano. Dove voto colore = voto famiglia la
   * sostituzione e' un no-op (stesso file), quindi nessuna regressione sui
   * prodotti gia' coerenti.
   */
  function updateAggregatedStarImage(visibleEl, rating) {
    if (rating == null) return false;
    var imgs = visibleEl.querySelectorAll('img[src*="/stars/"], img[class*="_stars"]');
    if (!imgs.length) return false;

    var exact = String(Math.round(rating * 10) / 10);
    var half = String(Math.round(rating * 2) / 2);
    var whole = String(Math.round(rating));
    var candidates = [];
    [exact, exact.replace('.', '_'), exact.replace('.', '-'),
     half, half.replace('.', '_'), half.replace('.', '-'),
     whole]
      .forEach(function (c) { if (candidates.indexOf(c) === -1) candidates.push(c); });

    Array.prototype.forEach.call(imgs, function (img) {
      var src = img.getAttribute('src') || '';
      var m = src.match(/^(.*\/)([^\/]+)\.svg(\?.*)?$/);
      if (!m) return;
      var base = m[1];
      var qs = m[3] || '';
      function setAlt() {
        try { img.setAttribute('alt', 'Average Rating: ' + String(rating)); } catch (e) {}
      }
      (function tryNext(idx) {
        if (idx >= candidates.length) return; // nessun file valido: stelle invariate
        var candidate = base + candidates[idx] + '.svg' + qs;
        if (candidate === src) { setAlt(); return; }
        var probe = new Image();
        probe.onload = function () {
          img.setAttribute('src', candidate);
          setAlt();
          if (DEBUG) log('star image aggiornata:', m[2] + '.svg ->', candidates[idx] + '.svg');
        };
        probe.onerror = function () { tryNext(idx + 1); };
        probe.src = candidate;
      })(0);
    });
    return true;
  }

  /*
   * Fallback per template che usano un layer "stelle piene" con width inline
   * in % (es. 80% = 4/5) sopra le stelle vuote, invece dell'immagine singola.
   * Cerchiamo un elemento star/rating-ish con width % inline e la ricalcoliamo.
   */
  function updateAggregatedStarFill(visibleEl, rating) {
    if (rating == null) return false;
    var pct = Math.max(0, Math.min(100, (rating / 5) * 100));
    var candidates = visibleEl.querySelectorAll('[class*="star"], [class*="ratx"], [class*="rating"]');
    for (var i = 0; i < candidates.length; i++) {
      var el = candidates[i];
      var w = el.style && el.style.width;
      if (w && /%\s*$/.test(w)) {
        var cur = parseFloat(w);
        if (isFinite(cur) && cur > 0 && cur <= 100) {
          el.style.width = pct + '%';
          if (DEBUG) log('star fill aggiornato:', cur + '% ->', pct + '%', '(', el.className, ')');
          return true;
        }
      }
    }
    if (DEBUG) log('star fill: nessun layer width-% trovato');
    return false;
  }

  /*
   * Iniezione rating sintetico nel widget visibile.
   *
   * NB: usiamo un TreeWalker sui TEXT NODE perche il markup Feedaty puo' avere
   * il numero rating come text node FIGLIO DIRETTO di un elemento che pero ha
   * altri children. Esempio osservato sul widget Striscia-full-nologo:
   *
   *   <div class="fdt_productStriscia-full-nologo_ratx">5,0<span>/5</span></div>
   *
   * Iterando solo "elementi senza children" (vecchio approccio) il "5,0" non
   * veniva mai matchato, perche il <div> ha lo <span> /5 come child.
   *
   * Sostituiamo:
   *   - rating: primo text node che matcha "<num>" o "<num>/5" con num in 0..5
   *   - count : primo text node che matcha "(<int>)", "<int> recensioni", o
   *             un intero standalone in [0..99999]
   *   - stelle: updateAggregatedStarImage (img SVG), fallback
   *             updateAggregatedStarFill (layer width-%)
   */
  function injectAggregatedRating(visibleEl, rating, count) {
    if (rating == null && count == null) return;
    var ratingDone = rating == null;
    var countDone = count == null;

    var walker = document.createTreeWalker(visibleEl, NodeFilter.SHOW_TEXT, null);
    var node;
    while ((node = walker.nextNode())) {
      if (ratingDone && countDone) break;
      var raw = node.nodeValue || '';
      var t = raw.trim();
      if (!t) continue;

      if (!ratingDone) {
        var mr = t.match(/^(\d(?:[.,]\d{1,2})?)(?:\s*\/\s*5)?$/);
        if (mr) {
          var v = num(mr[1]);
          if (v != null && v >= 0 && v <= 5) {
            var sep = mr[1].indexOf(',') > -1 ? ',' : '.';
            var rstr = String(rating).replace('.', sep);
            if (t.indexOf('/') > -1) rstr += '/5';
            node.nodeValue = raw.replace(t, rstr);
            ratingDone = true;
            continue;
          }
        }
      }
      if (!countDone) {
        var mc1 = t.match(/^\((\d{1,6})\)$/);
        if (mc1) { node.nodeValue = raw.replace(t, '(' + count + ')'); countDone = true; continue; }
        var mc2 = t.match(/(\d{1,6})\s*(recension|review|valutazion|opinion|bewertung|avis|kommentar)/i);
        if (mc2) { node.nodeValue = raw.replace(mc2[1], String(count)); countDone = true; continue; }
        var mc3 = t.match(/^(\d{1,6})$/);
        if (mc3) {
          var cv = parseInt(mc3[1], 10);
          if (cv >= 0 && cv < 100000) {
            node.nodeValue = raw.replace(t, String(count));
            countDone = true;
            continue;
          }
        }
      }
    }

    try {
      if (!updateAggregatedStarImage(visibleEl, rating)) {
        updateAggregatedStarFill(visibleEl, rating);
      }
    } catch (e) {}
    visibleEl.setAttribute('data-feedaty-aggregated-rating', rating == null ? '' : String(rating));
    visibleEl.setAttribute('data-feedaty-aggregated-count', count == null ? '' : String(count));
  }

  /*
   * Iniezione review nel carosello visibile (solo nel track orizzontale).
   *
   * Le card dei sibling si appendono solo a .fdt_carousel__track (o equivalente)
   * individuato da findFeedatyCarouselTrack. Se il track non c'e', non iniettiamo
   * nulla nella PDP: le review aggregate restano nel tab "Tutti i colori" in modal.
   */
  /*
   * Markup target del carosello Feedaty (variant recensioni-carosello-1):
   *
   *   .feedaty_widget [data-type="carouselproduct"]
   *     .fdt_widget_wrapper
   *       .fdt_widget_summary       (logo + media voto + count)
   *       .fdt_carousel
   *         .fdt_carousel_controls
   *           button.slide-prev
   *           button.slide-next
   *         .fdt_carousel__screen   (overflow:hidden)
   *           a.fdt_reviews
   *             #fdt_block.fdt_rev_block
   *               .fdt_carousel__track  [transform: translateX(-N px)]
   *                 .fdt_carousel__item   <- LA SLIDE / CARD
   *                 .fdt_carousel__item
   *                 ...
   *
   * Iniettiamo le slide SOLO nel .fdt_carousel__track (o equivalente) che contiene
   * gia' le slide .fdt_carousel__item. Mai su .fdt_carousel o sul root widget:
   * i fallback vecchi matchavano [class*="carousel"] e appendevano le card come
   * fratelli dello screen, "esplodendo" il layout della PDP.
   */
  function findFeedatyCarouselTrack(visibleEl) {
    if (!visibleEl || !visibleEl.querySelectorAll) return null;
    var tracks = visibleEl.querySelectorAll('[class*="carousel__track"]');
    var i;
    for (i = 0; i < tracks.length; i++) {
      var tr = tracks[i];
      if (tr.querySelector('.fdt_carousel__item, [class*="fdt_carousel__item"], [class*="carousel__item"]')) {
        return tr;
      }
    }
    var screen = visibleEl.querySelector('[class*="carousel__screen"]');
    if (screen) {
      var inner = screen.querySelector('[class*="carousel__track"]');
      if (inner) return inner;
    }
    if (tracks.length === 1) return tracks[0];
    return null;
  }

  function injectAggregatedReviews(visibleEl, reviewNodes, debugInfo) {
    if (!reviewNodes || !reviewNodes.length) return 0;

    // Idempotenza: rimuovi iniezioni precedenti (utile se l'aggregator
    // partisse due volte o se restano residui da rendering precedenti).
    var prev = visibleEl.querySelectorAll('[data-feedaty-aggregated="1"]');
    for (var p = 0; p < prev.length; p++) {
      if (prev[p].parentNode) prev[p].parentNode.removeChild(prev[p]);
    }

    var container = findFeedatyCarouselTrack(visibleEl);
    if (!container) {
      if (DEBUG) {
        log(
          'injectAggregatedReviews: nessun track carosello sicuro, salto iniezione inline (le review restano in modal)',
          visibleEl && visibleEl.getAttribute ? visibleEl.getAttribute('data-id') : ''
        );
      }
      if (debugInfo && typeof debugInfo === 'object') debugInfo.containerSrc = 'skipped-no-track';
      return 0;
    }
    var containerSrc = 'carousel-track';

    var added = 0;
    reviewNodes.forEach(function (n) {
      try {
        var clone = n.cloneNode(true);
        clone.setAttribute('data-feedaty-aggregated', '1');
        container.appendChild(clone);
        added++;
      } catch (e) {}
    });

    // Se abbiamo iniettato in un carosello Feedaty, prendiamoci il controllo
    // delle frecce: il widget JS originale conosce solo le slide del prodotto
    // corrente, le nostre nuove le ignorerebbe.
    if (containerSrc === 'carousel-track' && added > 0) {
      try { rebindCarouselControls(visibleEl, container, debugInfo); }
      catch (e) { log('rebindCarouselControls failed', e); }
    }

    try { window.dispatchEvent(new Event('resize')); } catch (e) {}

    if (debugInfo && typeof debugInfo === 'object') {
      debugInfo.containerSrc = containerSrc;
      debugInfo.containerClass =
        container && typeof container.className === 'string' ? container.className.slice(0, 120) : null;
      debugInfo.existingReviews = visibleEl.querySelectorAll(REVIEW_SELECTORS).length;
    }
    return added;
  }

  /*
   * Sostituisce gli handler Prev/Next del carosello Feedaty: clona i bottoni
   * (rimuovendo tutti i listener), resetta transform, e bindo nuovi handler
   * che scorrono il track di "step" pixel basandosi sulla larghezza media
   * delle slide e fermandosi al limite reale del track.
   */
  function rebindCarouselControls(visibleEl, track, debugInfo) {
    var screen = visibleEl.querySelector('[class*="carousel__screen"]');
    var prevBtn = visibleEl.querySelector('[class*="slide-prev"]');
    var nextBtn = visibleEl.querySelector('[class*="slide-next"]');
    if (!screen || !prevBtn || !nextBtn) {
      if (debugInfo) debugInfo.rebind = 'missing-elements';
      return;
    }

    // Reset transform prima di cambiare numero di slide.
    track.style.transform = 'translateX(0px)';

    // Sostituisci i bottoni con cloni "puliti" (perdono i listener Feedaty).
    function replaceBtn(btn) {
      var c = btn.cloneNode(true);
      c.removeAttribute('style');
      c.style.pointerEvents = 'auto';
      btn.parentNode.replaceChild(c, btn);
      return c;
    }
    var prev = replaceBtn(prevBtn);
    var next = replaceBtn(nextBtn);

    var pos = 0;

    function stepPx() {
      var first = track.children[0];
      if (first && first.getBoundingClientRect) {
        var w = first.getBoundingClientRect().width;
        if (w > 0) return w;
      }
      return Math.max(200, screen.clientWidth / 4);
    }

    function maxPx() {
      return Math.max(0, track.scrollWidth - screen.clientWidth);
    }

    function updateButtonsState() {
      var max = maxPx();
      prev.style.opacity = pos <= 0 ? '0.4' : '1';
      next.style.opacity = pos >= max ? '0.4' : '1';
      prev.style.pointerEvents = pos <= 0 ? 'none' : 'auto';
      next.style.pointerEvents = pos >= max ? 'none' : 'auto';
    }

    function moveBy(delta) {
      pos = Math.max(0, Math.min(maxPx(), pos + delta));
      track.style.transform = 'translateX(-' + pos + 'px)';
      updateButtonsState();
    }

    prev.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      moveBy(-stepPx());
    });
    next.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (e.stopImmediatePropagation) e.stopImmediatePropagation();
      moveBy(stepPx());
    });
    window.addEventListener('resize', updateButtonsState);
    updateButtonsState();

    if (debugInfo) {
      debugInfo.rebind = 'ok';
      debugInfo.trackChildrenAfter = track.children.length;
      debugInfo.maxPx = maxPx();
    }
  }

  function isFeedatyReady() {
    try {
      return typeof FeedatyWidget !== 'undefined' && FeedatyWidget && FeedatyWidget.firstLoad === false;
    } catch (e) {
      return false;
    }
  }

  function whenFeedatyReady(cb) {
    if (isFeedatyReady()) { cb(); return; }
    var fired = false;
    var iv = null;
    function once() {
      if (fired) return;
      fired = true;
      if (iv) { clearInterval(iv); iv = null; }
      document.removeEventListener('FeedatyLoaded', once);
      cb();
    }
    document.addEventListener('FeedatyLoaded', once);
    var startedAt = Date.now();
    iv = setInterval(function () {
      if (isFeedatyReady()) { once(); return; }
      if (Date.now() - startedAt > MAX_FEEDATY_READY_WAIT_MS) {
        clearInterval(iv); iv = null;
        log('timeout waiting for FeedatyWidget to be ready, proceeding anyway');
        once();
      }
    }, POLL_INTERVAL_MS);
  }

  /*
   * Widget "<!-- no feedbacks -->": con 0 recensioni proprie Feedaty rende
   * solo la modale (display:none), nessun contenuto visibile (h:0). Se la
   * famiglia colore HA recensioni, cloniamo il template html di un sibling
   * con recensioni dentro il widget: variant identico, gli stili Feedaty si
   * applicano da soli, e injectAggregatedRating sistema numeri e stelle.
   */
  function widgetHasVisibleContent(visibleEl) {
    for (var el = visibleEl.firstElementChild; el; el = el.nextElementSibling) {
      if (!el.classList || !el.classList.contains('feedaty_modal_merch')) return true;
    }
    return false;
  }

  function injectSyntheticWidgetFromSibling(visibleEl, siblingHtml) {
    if (!siblingHtml || typeof siblingHtml !== 'string') return false;
    if (visibleEl.querySelector('[data-feedaty-synthetic="1"]')) return true; // idempotente
    var doc = parseHtmlToDoc(siblingHtml);
    if (!doc || !doc.body) return false;
    var wrapper = document.createElement('div');
    wrapper.setAttribute('data-feedaty-synthetic', '1');
    Array.prototype.slice.call(doc.body.childNodes).forEach(function (n) {
      try { wrapper.appendChild(document.importNode(n, true)); } catch (e) {}
    });
    // JSON-LD del sibling = dati del prodotto sbagliato: via.
    Array.prototype.forEach.call(wrapper.querySelectorAll('script'), function (s) {
      if (s.parentNode) s.parentNode.removeChild(s);
    });
    // Anchor del sibling: niente salto a feedaty.com del colore sbagliato.
    Array.prototype.forEach.call(wrapper.querySelectorAll('a[href]'), function (a) {
      a.setAttribute('href', 'javascript:void(0);');
      a.removeAttribute('target');
    });
    if (!wrapper.firstElementChild) return false;
    visibleEl.insertBefore(wrapper, visibleEl.firstChild);

    // Click: con "no feedbacks" il toggle nativo Feedaty potrebbe non essere
    // stato installato. Il setTimeout evita di pestarlo quando invece esiste
    // (apriremmo+chiuderemmo): se dopo 60ms la modale e' ancora chiusa, la
    // apriamo noi. L'apertura via style scatena observeModalOpen -> portale.
    var ownModal = visibleEl.querySelector('.feedaty_modal_merch');
    if (ownModal) visibleEl.__faOwnModal = ownModal;
    wrapper.addEventListener('click', function () {
      var modal = visibleEl.__faOwnModal;
      if (!modal) return;
      setTimeout(function () {
        try {
          var cs = window.getComputedStyle(modal);
          if (cs.display === 'none') modal.style.display = 'block';
        } catch (e) {
          modal.style.display = 'block';
        }
      }, 60);
    });
    return true;
  }

  /*
   * Scrive il voto/numero recensioni aggregato nel blocco
   * <script id="product-schema-ld"> gia' presente in sections/product.liquid,
   * cosi' Google puo' mostrare le stelline nei risultati di ricerca (le
   * iniettiamo qui perche' a render time del Liquid il dato Feedaty non
   * esiste ancora: arriva solo dopo che il widget carica, lato browser).
   *
   * productId deve combaciare con il data-product-id dello script: e' la
   * protezione contro i widget "product" di prodotti diversi dal corrente
   * presenti nella stessa pagina (es. gli upsell, che riusano lo stesso
   * snippets/product-price.liquid e quindi lo stesso data-type="product").
   * Senza questo controllo scriveremmo nello schema del prodotto visitato il
   * voto di un prodotto correlato, invece del suo.
   *
   * Non scriviamo nulla se non abbiamo un voto o se il count e' zero: Google
   * vieta esplicitamente di marcare un prodotto senza recensioni reali.
   */
  function injectAggregateRatingSchema(productId, rating, count) {
    if (rating == null || !count || count <= 0) return;
    var script = document.querySelector('script[type="application/ld+json"]#product-schema-ld');
    if (!script) return; // pagina senza questo blocco (non e' una PDP, o markup cambiato)
    if (script.getAttribute('data-rating-injected') === '1') return; // idempotente
    if (String(script.getAttribute('data-product-id')) !== String(productId)) return;
    var data;
    try { data = JSON.parse(script.textContent); } catch (e) { return; }
    data.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Math.round(rating * 10) / 10,
      reviewCount: count
    };
    try {
      script.textContent = JSON.stringify(data);
      script.setAttribute('data-rating-injected', '1');
    } catch (e) {}
  }

  function processWidget(visibleEl, otherIds, debugBucket, globalState, productById) {
    var sku = visibleEl.getAttribute('data-sku');
    var dataId = visibleEl.getAttribute('data-id');
    var variant = visibleEl.getAttribute('data-variant');
    var type = visibleEl.getAttribute('data-type');
    productById = productById || {};

    return waitForCaptureForWidget(visibleEl, CAPTURE_WAIT_MS).then(function (currentCap) {
      if (!currentCap) {
        log('no capture for current sku', sku, '(widget', dataId, type, variant, ') - skipping');
        debugBucket.push({ dataId: dataId, sku: sku, variant: variant, type: type, error: 'no-capture-current' });
        return;
      }

      var siblingUrls = otherIds.map(function (s) { return { sku: s, url: urlForSku(currentCap.url, s) }; });
      log('widget', dataId, type, variant, 'sku=', sku, 'fetching', siblingUrls.length, 'sibling templates');

      return Promise.all(siblingUrls.map(function (it) {
        return fetchTemplate(it.url).then(function (res) {
          res.sku = it.sku;
          return res;
        });
      })).then(function (results) {
        var perSibling = [];
        var currentEntry = pickEntry(currentCap.json);
        var currentStats = extractEntryStats(currentEntry);
        if (globalState && currentEntry && currentEntry.settings) {
          var ou0 = openOverlayUrlFromSettings(currentEntry.settings);
          if (ou0 && !globalState.openOverlayBase) {
            globalState.openOverlayBase = normalizeFeedatyReviewsCanonicalUrl(ou0);
          }
          rememberOverlayForProduct(globalState, sku, currentEntry.settings, currentEntry.html);
        }
        perSibling.push({
          sku: sku, isCurrent: true, ok: true,
          rating: currentStats.rating, count: currentStats.count,
          reviewNodesCount: (currentStats.reviewNodes || []).length,
          srcs: currentStats._src,
          settingsKeys: currentStats.settings ? Object.keys(currentStats.settings) : [],
          htmlPreview: (currentEntry && currentEntry.html ? currentEntry.html.slice(0, 800) : null),
          reviewMatch: debugReviewMatchSummary(currentEntry && currentEntry.html)
        });

        var siblingReviewNodes = [];
        var siblingModalItems = [];
        var siblingMetaMissingLogged = {};
        results.forEach(function (r, idx) {
          if (r.error) {
            perSibling.push({ sku: r.sku, isCurrent: false, ok: false, error: r.error });
            return;
          }
          var entry = pickEntry(r.json);
          var st = extractEntryStats(entry);
          rememberOverlayForProduct(globalState, r.sku, entry && entry.settings, entry && entry.html);
          perSibling.push({
            sku: r.sku, isCurrent: false, ok: true,
            rating: st.rating, count: st.count,
            reviewNodesCount: (st.reviewNodes || []).length,
            srcs: st._src,
            settingsKeys: st.settings ? Object.keys(st.settings) : [],
            // Preview html solo per i primi 2 sibling, per non gonfiare l'oggetto debug.
            htmlPreview: idx < 2 && entry && entry.html ? entry.html.slice(0, 800) : null,
            reviewMatch: idx < 2 ? debugReviewMatchSummary(entry && entry.html) : undefined
          });
          siblingReviewNodes = siblingReviewNodes.concat(st.reviewNodes || []);
          var pidSib = String(r.sku || '').trim();
          var metaSib = productById[pidSib] || null;
          if (!metaSib && DEBUG && !siblingMetaMissingLogged[pidSib]) {
            siblingMetaMissingLogged[pidSib] = true;
            log('modal meta missing for sibling sku', pidSib, '(check data-sku vs feedaty-siblings-data ids)');
          }
          (st.reviewNodes || []).forEach(function (n) {
            siblingModalItems.push({ node: n, meta: metaSib, productId: pidSib });
          });
        });

        var agg = aggregateStats(
          perSibling
            .filter(function (s) { return s.ok; })
            .map(function (s) {
              return {
                rating: s.rating,
                count: s.count,
                reviewNodes: s.isCurrent ? [] : (s.reviewNodes || [])
              };
            })
        );
        // Reviews aggregate = solo quelle dei sibling, da appendere sopra/sotto
        // a quelle del widget visibile gia popolato dal prodotto corrente.
        var addedReviews = 0;
        var injectionInfo = {};

        // Widget vuoto ("no feedbacks") ma famiglia con recensioni:
        // costruiamo il widget clonando il template di un sibling-donatore.
        var syntheticInjected = false;
        if ((agg.count || 0) > 0 && !widgetHasVisibleContent(visibleEl)) {
          var donorEntry = null;
          for (var di = 0; di < results.length && !donorEntry; di++) {
            if (results[di].error) continue;
            var de = pickEntry(results[di].json);
            if (de && de.html && de.html.indexOf('no feedbacks') === -1) donorEntry = de;
          }
          if (donorEntry) {
            syntheticInjected = injectSyntheticWidgetFromSibling(visibleEl, donorEntry.html);
            if (DEBUG) log('widget', dataId, type, 'contenuto sintetico da sibling:', syntheticInjected);
          }
        }
        injectionInfo.synthetic = syntheticInjected;

        if (syntheticInjected && type === 'carouselproduct') {
          // Il carosello del donatore contiene gia' le sue card ma Feedaty JS
          // non ha inizializzato le frecce su questo markup: rebind nostro.
          var synTrack = findFeedatyCarouselTrack(visibleEl);
          if (synTrack) {
            try { rebindCarouselControls(visibleEl, synTrack, injectionInfo); } catch (e) {}
          }
        } else if (type === 'carouselproduct' || (currentStats.reviewNodes && currentStats.reviewNodes.length === 0 && siblingReviewNodes.length > 0)) {
          addedReviews = injectAggregatedReviews(visibleEl, siblingReviewNodes, injectionInfo);
        }
        // Rating aggregato: solo se sappiamo media e count consolidati.
        if (agg.rating != null || agg.count != null) {
          injectAggregatedRating(visibleEl, agg.rating, agg.count);
          if (type === 'product') {
            injectAggregateRatingSchema(sku, agg.rating, agg.count);
          }
        }

        // Per la modal globale: tieni il dataset di review piu' ricco.
        // I template "product" (Striscia) spesso non hanno card nel JSON; il carosello si.
        // Con piu' widget stesso SKU serve findCaptureForWidget cosi il job carosello
        // non legge il JSON della Striscia. Se solo Striscia ha nodi (raro), accumuliamo anche da type product.
        var widgetReviewsCount = (currentStats.reviewNodes || []).length + (siblingReviewNodes || []).length;
        log('widget', dataId, type, 'review pool size for modal candidate:', widgetReviewsCount);
        if (globalState && (type === 'carouselproduct' || type === 'product')) {
          var pidCurrent = String(sku || '').trim();
          var metaCurrent = productById[pidCurrent] || null;
          if (!metaCurrent && DEBUG) {
            log('modal meta missing for current sku', pidCurrent, '(check data-sku vs feedaty-siblings-data)');
          }
          var currentModalItems = (currentStats.reviewNodes || []).map(function (n) {
            return { node: n, meta: metaCurrent, productId: pidCurrent };
          });
          var widgetReviewItems = currentModalItems.concat(siblingModalItems);
          if (widgetReviewItems.length &&
              (!globalState.modalReviewItems || widgetReviewItems.length > globalState.modalReviewItems.length)) {
            globalState.modalReviewItems = widgetReviewItems;
            globalState.modalRating = agg.rating;
            globalState.modalCount = agg.count;
            log('widget', dataId, 'set as modal source with', widgetReviewItems.length, 'review items');
          }
        }

        debugBucket.push({
          dataId: dataId, sku: sku, variant: variant, type: type,
          aggregated: {
            rating: agg.rating, count: agg.count,
            addedReviewNodes: addedReviews,
            siblingReviewNodesCollected: siblingReviewNodes.length,
            injection: injectionInfo
          },
          visibleWidgetMatch: debugReviewMatchSummary(visibleEl.outerHTML),
          perSibling: perSibling,
          currentTemplateUrl: currentCap.url,
          siblingUrlSample: siblingUrls.slice(0, 3).map(function (it) { return it.url; })
        });
        log('widget', dataId, type, 'aggregated', { rating: agg.rating, count: agg.count, addedReviews: addedReviews, collected: siblingReviewNodes.length });
      });
    });
  }

  /*
   * MODAL "Tutte le opinioni" (#feedaty_modal_merch).
   *
   * Comportamento Feedaty originale: cliccando sul rating sintetico, sul logo
   * Feedaty del carosello, o su una slide del carosello, il widget JS apre la
   * modal e setta src dell'iframe #fdt_mp_frame su
   * https://www.feedaty.com/feedaty/reviews/<merchant>/<product-handle>?nocookie=true.
   * Quell'iframe e' cross-origin, NON manipolabile via DOM.
   *
   * Nostra strategia (lato browser, no backend):
   *   - Lasciamo l'iframe in DOM (feedaty.min.js continua a settargli src ad
   *     ogni click; ignoriamo l'errore se ce ne fosse uno).
   *   - Lo nascondiamo con display:none inline (vince sull'eventuale display
   *     impostato da Feedaty).
   *   - Iniettiamo dentro #fdt_frameContainer un nostro nodo
   *     #feedaty-aggregator-modal-content che mostra TUTTE le review
   *     aggregate di tutta la famiglia colore (gia raccolte
   *     dall'aggregator), con un mini summary in cima.
   *
   * Il chrome della modal (overlay, close button, animazione apertura/chiusura)
   * resta gestito da Feedaty, quindi UX e' identica a quella originale.
   */
  /*
   * Foglio tema assets/feedaty-aggregator-custom.css: URL passato da
   * layout/theme.liquid (__feedatyAggregatorAssetUrls.customCss). Lo appendiamo a
   * document.head DOPO #feedaty-aggregator-modal-style cosi le regole custom vincono.
   */
  function ensureAggregatorCustomCssLink() {
    try {
      var urls = typeof window.__feedatyAggregatorAssetUrls !== 'undefined' ?
        window.__feedatyAggregatorAssetUrls : null;
      var url = urls && urls.customCss;
      if (!url || !document.querySelector('.feedaty_widget')) return;
      var lnk = document.getElementById('feedaty-aggregator-custom-css');
      if (!lnk) {
        lnk = document.createElement('link');
        lnk.id = 'feedaty-aggregator-custom-css';
        lnk.rel = 'stylesheet';
        lnk.href = url;
      }
      /* Sposta in coda a <head>: cosi vince sui fogli Feedaty inseriti dopo al primo append. */
      document.head.appendChild(lnk);
    } catch (e) {}
  }

  /*
   * Stili modal: iniettati UNA volta sola all'inizializzazione, riusati da
   * tutte le modal in pagina.
   */
  function ensureModalStyles() {
    if (!document.getElementById('feedaty-aggregator-modal-style')) {
      var style = document.createElement('style');
      style.id = 'feedaty-aggregator-modal-style';
    style.textContent =
      // Tab bar in alto al frame container.
      '.fa-modal-tabs{box-sizing:border-box;display:flex;flex-wrap:nowrap;overflow-x:auto;background:#fff;border-bottom:1px solid #e5e5e5;padding:0 0.5rem;position:sticky;top:0;z-index:2;-webkit-overflow-scrolling:touch;scrollbar-width:thin;}' +
      '.fa-modal-tabs::-webkit-scrollbar{height:4px;}' +
      '.fa-modal-tabs::-webkit-scrollbar-thumb{background:#ccc;border-radius:2px;}' +
      '.fa-modal-tab{flex:0 0 auto;background:transparent;border:none;padding:0.75rem 1rem;cursor:pointer;font-family:inherit;font-size:0.875rem;line-height:1.2;color:#666;border-bottom:2px solid transparent;white-space:nowrap;transition:color .15s ease, border-color .15s ease;}' +
      '.fa-modal-tab:hover{color:#000;}' +
      '.fa-modal-tab.is-active{color:#000;border-bottom-color:#000;font-weight:600;}' +
      '.fa-modal-tab--current{font-style:italic;}' +
      // Il frame container e' alto ~100% nel CSS Feedaty; aggiungiamo tab + contenuto che
      // altrimenti spingono l'altezza oltre la modale. Flex + max-height evita overflow
      // verso il basso; scroll solo nell'area elenco / iframe.
      '.feedaty_modal_merch #fdt_frameContainer,' +
      '.feedaty_modal_merch #fdt_frameContainer.zopinionepop_merch{' +
      'display:flex!important;flex-direction:column!important;position:relative!important;' +
      'max-height:min(82vh,760px)!important;min-height:0!important;overflow:hidden!important;' +
      'box-sizing:border-box!important;}' +
      '.feedaty_modal_merch #fdt_frameContainer .fa-modal-tabs{position:relative!important;z-index:30!important;background:#fff!important;}' +
      '.feedaty_modal_merch #fdt_frameContainer .feedaty-aggregator-modal-content:not([hidden]){position:relative!important;z-index:20!important;}' +
      '.feedaty_modal_merch #fdt_frameContainer #fdt_mp_frame:not([hidden]){position:relative!important;z-index:10!important;}' +
      '.feedaty_modal_merch .fa-modal-tabs{flex-shrink:0!important;}' +
      '.feedaty_modal_merch .feedaty-aggregator-modal-content:not([hidden]){' +
      'flex:1 1 auto!important;min-height:0!important;max-height:none!important;' +
      'overflow-y:auto!important;-webkit-overflow-scrolling:touch!important;}' +
      '.feedaty_modal_merch #fdt_mp_frame:not([hidden]){' +
      'flex:1 1 auto!important;min-height:min(58vh,480px)!important;width:100%!important;' +
      'max-height:none!important;border:0!important;}' +
      // Custom content (tab "Tutti i colori"). Quando un tab di colore e' attivo, e' nascosto.
      '.feedaty-aggregator-modal-content{box-sizing:border-box;padding:1.5rem 1.75rem;overflow-y:auto;background:#fff;color:#222;font-family:inherit;}' +
      '.feedaty-aggregator-modal-content[hidden]{display:none;}' +
      '.feedaty-aggregator-modal-content .fa-modal-summary{font-size:1rem;line-height:1.4;margin:0 0 1.5rem 0;padding:0 0 1rem 0;border-bottom:1px solid #e5e5e5;}' +
      '.feedaty-aggregator-modal-content .fa-modal-summary strong{font-size:1.25rem;}' +
      '.feedaty-aggregator-modal-content .fa-modal-summary-note{font-size:0.8125rem;line-height:1.45;color:#666;margin:-0.5rem 0 1rem 0;padding:0;}' +
      '.feedaty-aggregator-modal-content .fa-modal-list{display:block;}' +
      '.feedaty-aggregator-modal-content .fa-modal-list .fdt_carousel__item{display:block !important;width:100% !important;max-width:none !important;flex:none !important;margin:0 0 1.25rem 0 !important;padding:0 0 1.25rem 0 !important;border-bottom:1px solid #f0f0f0 !important;}' +
      '.feedaty-aggregator-modal-content .fa-modal-list .fa-review-origin-wrap .fdt_carousel__item{margin:0 !important;padding:0 !important;border-bottom:none !important;}' +
      '.feedaty-aggregator-modal-content .fa-modal-list .fa-review-origin-wrap:last-child .fdt_carousel__item:last-child{border-bottom:none !important;}' +
      '.feedaty-aggregator-modal-content .fa-modal-list .fa-review-origin-wrap{box-sizing:border-box;margin:0 0 1.25rem 0;padding:0 0 1.25rem 0;border-bottom:1px solid #f0f0f0;}' +
      '.feedaty-aggregator-modal-content .fa-modal-list .fa-review-origin-wrap:last-child{border-bottom:none;margin-bottom:0;padding-bottom:0;}' +
      '.feedaty-aggregator-modal-content .fa-review-origin{display:flex;flex-direction:row;align-items:flex-start;gap:0.75rem;margin:0 0 0.85rem 0;}' +
      '.feedaty-aggregator-modal-content .fa-review-origin__img{width:56px;height:56px;object-fit:cover;border-radius:6px;flex-shrink:0;border:1px solid #eee;background:#f7f7f7;}' +
      '.feedaty-aggregator-modal-content .fa-review-origin__img--placeholder{width:56px;height:56px;flex-shrink:0;border-radius:6px;border:1px dashed #ddd;background:#fafafa;}' +
      '.feedaty-aggregator-modal-content .fa-review-origin__text{min-width:0;flex:1;}' +
      '.feedaty-aggregator-modal-content .fa-review-origin__color{font-weight:600;font-size:0.9rem;color:#222;line-height:1.3;}' +
      '.feedaty-aggregator-modal-content .fa-review-origin__title{font-size:0.8rem;color:#666;line-height:1.35;margin-top:0.2rem;}' +
      '.feedaty-aggregator-modal-content .fa-modal-list .fdt_carousel__item:last-child{border-bottom:none !important;}' +
      '.feedaty-aggregator-modal-content .fa-modal-list .fdt_content{padding:0 !important;}' +
      '.feedaty-aggregator-modal-content .fa-modal-list .fdt_rev{padding:0 !important;}' +
      '.feedaty-aggregator-modal-content .fa-modal-pager{display:flex;flex-wrap:wrap;align-items:center;justify-content:center;gap:0.75rem 1rem;margin:1.25rem 0 0 0;padding:1rem 0 0 0;border-top:1px solid #e5e5e5;font-size:0.875rem;color:#444;}' +
      '.feedaty-aggregator-modal-content .fa-modal-pager__btn{font-family:inherit;font-size:0.875rem;padding:0.45rem 0.9rem;cursor:pointer;border:1px solid #ccc;background:#fff;border-radius:4px;color:#222;}' +
      '.feedaty-aggregator-modal-content .fa-modal-pager__btn:hover:not(:disabled){background:#f5f5f5;border-color:#bbb;}' +
      '.feedaty-aggregator-modal-content .fa-modal-pager__btn:disabled{cursor:not-allowed;}' +
      '.feedaty-aggregator-modal-content .fa-modal-pager__info{min-width:12rem;text-align:center;}' +
      // Iframe Feedaty: lo nascondiamo via attribute hidden quando tab "Tutti" e' attivo.
      '#fdt_mp_frame[hidden]{display:none !important;}';
      document.head.appendChild(style);
    }
    ensureAggregatorCustomCssLink();
  }

  /*
   * Feedaty risponde 301 da /feedaty/reviews/{shop}/{slug} verso
   * /recensioni/{shop}/{slug} (200). Puntiamo subito al path canonico.
   */
  function normalizeFeedatyReviewsCanonicalUrl(url) {
    if (!url || typeof url !== 'string') return url;
    return url.replace(/\/feedaty\/reviews\//gi, '/recensioni/');
  }

  /*
   * Ultimo segmento path = slug prodotto (handle nel path), es. cappello-...-blu-mora.
   * Non l'id numerico Shopify (e' solo data-sku sul widget).
   */
  function buildSiblingIframeUrl(templateSrc, productSlug) {
    if (!templateSrc || productSlug == null || productSlug === '') return null;
    var src = String(templateSrc).trim();
    if (/^about:/i.test(src)) return null;
    var seg = encodeURIComponent(String(productSlug));
    var re = /(\/(?:feedaty\/reviews|recensioni)\/[^/?#]+\/)([^/?#]+)/i;
    if (re.test(src)) {
      return normalizeFeedatyReviewsCanonicalUrl(
        src.replace(re, function (_, prefix) {
          return prefix + seg;
        })
      );
    }
    try {
      var u = new URL(src);
      var parts = u.pathname.replace(/\/$/, '').split('/').filter(Boolean);
      if (parts.length) {
        parts[parts.length - 1] = seg;
        u.pathname = '/' + parts.join('/');
        return normalizeFeedatyReviewsCanonicalUrl(u.toString());
      }
    } catch (e) {}
    return null;
  }

  function ensureFeedatyOverlayQuery(url) {
    if (!url) return url;
    if (/[?&]nocookie=/.test(url)) return url;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'nocookie=true';
  }

  function mergeFeedatyParamsFromReference(newUrl, refUrl) {
    if (!newUrl || !refUrl) return newUrl;
    try {
      var dst = new URL(newUrl, 'https://www.feedaty.com');
      var ref = new URL(refUrl, 'https://www.feedaty.com');
      ['gui', 'l', 'lang'].forEach(function (k) {
        if (!dst.searchParams.has(k) && ref.searchParams.has(k)) {
          dst.searchParams.set(k, ref.searchParams.get(k));
        }
      });
      return dst.toString();
    } catch (e) {
      return newUrl;
    }
  }

  function absolutizeFeedatyUrl(url) {
    if (!url || typeof url !== 'string') return url;
    var t = url.trim();
    if (!t) return t;
    if (/^https?:\/\//i.test(t)) return t;
    if (/^\/\//.test(t)) return 'https:' + t;
    try {
      return new URL(t, 'https://www.feedaty.com').href;
    } catch (e) {
      return t;
    }
  }

  function openOverlayUrlFromSettings(settings) {
    if (!settings || typeof settings !== 'object') return null;
    var v =
      settings.OpenOverlayURL ||
      settings.openOverlayURL ||
      settings.OpenOverlayUrl;
    if (v && typeof v === 'string' && v.trim()) return v.trim();
    try {
      var keys = Object.keys(settings);
      for (var i = 0; i < keys.length; i++) {
        var name = keys[i];
        if (/overlay/i.test(name) && /url/i.test(name)) {
          var cand = settings[name];
          if (cand && typeof cand === 'string' && cand.trim()) return cand.trim();
        }
      }
    } catch (e) {}
    return null;
  }

  function openOverlayUrlFromHtml(html) {
    if (!html || typeof html !== 'string') return null;
    var m = html.match(/https?:\/\/[^"'\\s<>]+(?:\/feedaty\/reviews\/|\/recensioni\/)[^"'\\s<>?]+/i);
    if (m && m[0]) return m[0].replace(/\/$/, '');
    return null;
  }

  function rememberOverlayForProduct(globalState, productId, settings, html) {
    if (!globalState || productId == null || productId === '') return;
    var ou = openOverlayUrlFromSettings(settings) || openOverlayUrlFromHtml(html);
    if (!ou) return;
    ou = normalizeFeedatyReviewsCanonicalUrl(ou);
    if (!globalState.overlayUrlByProductId) globalState.overlayUrlByProductId = {};
    var k = String(productId);
    if (!globalState.overlayUrlByProductId[k]) {
      globalState.overlayUrlByProductId[k] = ou;
      log('modal: OpenOverlayURL for product', k, ':', ou.slice(0, 96));
    }
  }

  /*
  * Con la modale portata su <body> i click su X/backdrop non risalgono piu'
  * al .feedaty_widget (dove Feedaty tiene il toggle). Reinoltriamo il click
  * al widget di origine; fallback display:none se il toggle non scatta.
  */
  function installPortaledModalCloseHandlers(modal) {
    if (modal.__faPortalCloseInstalled) return;
    modal.__faPortalCloseInstalled = true;

    function closeModal(ev) {
      ev.preventDefault();
      ev.stopPropagation();
      var w = modal.__faOriginWidget;
      if (w) {
        try {
          w.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
        } catch (e) {}
      }
      // Failsafe: se il toggle Feedaty non ha chiuso, chiudiamo a mano.
      setTimeout(function () {
        try {
          var cs = window.getComputedStyle(modal);
          if (cs.display !== 'none') modal.style.display = 'none';
        } catch (e) {
          modal.style.display = 'none';
        }
      }, 0);
    }

    modal.addEventListener('click', function (ev) {
      var t = ev.target;
      if (!t) return;
      // X di chiusura
      if (t.closest && t.closest('.feedaty_close_merch')) { closeModal(ev); return; }
      // Backdrop = click fuori dal box bianco
      if (!t.closest || !t.closest('.modal_popup_merch')) { closeModal(ev); }
    }, true); // capture: cosi' battiamo eventuali stopPropagation interni
  }

  /*
   * Portale per TUTTE le modali prodotto, indipendente dall'esito
   * dell'aggregazione: anche su prodotti senza siblings, con 0-1 recensioni
   * o con fetch falliti, la modale nativa esce dagli stacking context del
   * tema (incluso il transform:scale su .feedaty_widget).
   */
  function portalProductModalsOnOpen() {
    var modals = document.querySelectorAll('.feedaty_modal_merch, [id="feedaty_modal_merch"]');
    for (var i = 0; i < modals.length; i++) {
      var modal = modals[i];
      if (modal.__faModalPortalObserved) continue;
      var w = modal.closest ? modal.closest('.feedaty_widget') : null;
      if (!w) continue;
      var t = (w.getAttribute('data-type') || '').toLowerCase();
      if (t !== 'product' && t !== 'carouselproduct') continue;
      modal.__faModalPortalObserved = true;
      modal.__faOriginWidget = w;
      (function (m) {
        observeModalOpen(m, function () {
          if (m.parentNode !== document.body) {
            try { document.body.appendChild(m); } catch (e) {}
          }
          installPortaledModalCloseHandlers(m);
        });
      })(modal);
    }
  }

  /*
   * Mobile (e variant slim): Feedaty non installa il toggle della modale e
   * l'anchor target=_blank naviga verso feedaty.com. Intercettiamo il click
   * su ogni widget prodotto: blocchiamo la navigazione e, se dopo 60ms la
   * modale e' ancora chiusa (= toggle Feedaty assente), la apriamo noi.
   * Su desktop il toggle Feedaty apre la modale e il fallback e' un no-op.
   *
   * ev.isTrusted: i click sintetici di installPortaledModalCloseHandlers
   * (chiusura via dispatchEvent sul widget) hanno isTrusted=false e vanno
   * ignorati, altrimenti il fallback riaprirebbe la modale appena chiusa.
   */
  function installWidgetClickToModal() {
    var widgets = findVisibleProductWidgets();
    for (var i = 0; i < widgets.length; i++) {
      (function (w) {
        if (w.__faClickToModalInstalled) return;
        w.__faClickToModalInstalled = true;
        w.addEventListener('click', function (ev) {
          if (!ev.isTrusted) return;
          var modal = w.__faOwnModal || w.querySelector('.feedaty_modal_merch');
          if (modal) w.__faOwnModal = modal;
          if (!modal) return;
          // Blocca la navigazione dell'anchor Feedaty (target _blank).
          var a = ev.target && ev.target.closest ? ev.target.closest('a[href]') : null;
          if (a && /feedaty\.com/i.test(a.getAttribute('href') || '')) {
            ev.preventDefault();
          }
          setTimeout(function () {
            try {
              if (window.getComputedStyle(modal).display === 'none') {
                modal.style.display = 'block';
              }
            } catch (e) {
              modal.style.display = 'block';
            }
          }, 60);
        }, true); // capture: il preventDefault arriva prima della navigazione
      })(widgets[i]);
    }
  }

  /*
   * Popola UNA modal Feedaty con il contenuto custom aggregato. Restituisce
   * true se la modal e' stata processata.
   *
   * IMPORTANTE: in pagina ci sono PIU di una modal feedaty_modal_merch (il
   * widget JS Feedaty ne crea una per OGNI widget renderizzato, tutte con
   * stesso id "feedaty_modal_merch" — id duplicati ma e' colpa loro). Al click
   * su un widget si apre la SUA modal, non una globale. Quindi dobbiamo
   * applicare la sostituzione a TUTTE le modal in pagina, non solo alla prima.
   *
   * Layout nel #fdt_frameContainer: .fa-modal-tabs, .feedaty-aggregator-modal-content,
   * iframe#fdt_mp_frame. Tab colore: data-fa-target = handle prodotto Shopify (slug in
   * /recensioni/{shop}/{handle}). All'apertura, reset tab "Tutti i colori" (observeModalOpen).
   */
  function populateOneModal(modal, reviewInput, rating, count, siblings, currentHandle, openOverlayBase, overlayUrlByProductId, overlayUrlByHandle) {
    var frameContainer = modal.querySelector('#fdt_frameContainer');
    if (!frameContainer) return false;

    var iframe = frameContainer.querySelector('#fdt_mp_frame');

    // Tab bar (riusa se esistente per evitare flicker su re-render).
    var tabs = frameContainer.querySelector('.fa-modal-tabs');
    if (!tabs) {
      tabs = document.createElement('div');
      tabs.className = 'fa-modal-tabs';
      tabs.setAttribute('role', 'tablist');
      frameContainer.insertBefore(tabs, frameContainer.firstChild);
    }
    tabs.innerHTML = '';

    // Tab "Tutti i colori" (default attivo).
    var allBtn = document.createElement('button');
    allBtn.type = 'button';
    allBtn.className = 'fa-modal-tab is-active';
    allBtn.setAttribute('data-fa-target', 'all');
    allBtn.textContent = 'Tutti i colori';
    tabs.appendChild(allBtn);

    // Un tab per ogni sibling (= un colore = un iframe Feedaty originale).
    if (Array.isArray(siblings)) {
      siblings.forEach(function (s) {
        if (!s || !s.handle) return;
        var btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'fa-modal-tab';
        if (s.isCurrent || String(s.handle) === String(currentHandle || '')) {
          btn.className += ' fa-modal-tab--current';
        }
        btn.setAttribute('data-fa-target', s.handle);
        btn.textContent = s.color || s.title || s.handle;
        tabs.appendChild(btn);
      });
    }

    // Custom content "Tutti i colori" (review aggregate).
    var custom = frameContainer.querySelector('.feedaty-aggregator-modal-content');
    if (!custom) {
      custom = document.createElement('div');
      custom.className = 'feedaty-aggregator-modal-content';
      custom.setAttribute('data-feedaty-aggregated', 'modal');
      // Inserisci prima dell'iframe (cosi appare sopra in flow normale).
      if (iframe && iframe.parentNode === frameContainer) {
        frameContainer.insertBefore(custom, iframe);
      } else {
        frameContainer.appendChild(custom);
      }
    }
    custom.innerHTML = '';
    custom.removeAttribute('hidden');

    var summary = document.createElement('div');
    summary.className = 'fa-modal-summary';
    var ratingTxt = rating != null ? String(rating).replace('.', ',') : '\u2013';
    var countTxt = count != null ? String(count) : '\u2013';
    summary.innerHTML =
      '<strong>Tutte le recensioni del modello</strong><br/>' +
      'Media voto: <strong>' + ratingTxt + '/5</strong> &middot; ' +
      'Totale recensioni: <strong>' + countTxt + '</strong>';
    custom.appendChild(summary);

    var list = document.createElement('div');
    list.className = 'fa-modal-list';

    var normalizedItems = normalizeModalReviewInput(reviewInput);
    var allReviewClones = [];
    normalizedItems.forEach(function (item) {
      if (!item || !item.node) return;
      try {
        allReviewClones.push(wrapReviewCardWithOrigin(item.node, item.meta));
      } catch (e) {}
    });

    var totalReviewsNum = null;
    if (count != null && count !== '') {
      var _p = parseInt(String(count), 10);
      if (isFinite(_p) && _p >= 0) totalReviewsNum = _p;
    }
    var totalReviewsKnown = totalReviewsNum != null;
    var previewCount = allReviewClones.length;
    if (totalReviewsKnown && previewCount > 0 && previewCount < totalReviewsNum) {
      var note = document.createElement('p');
      note.className = 'fa-modal-summary-note';
      note.textContent =
        'Qui sotto ci sono solo le ' + previewCount + ' recensioni pi\u00f9 recenti che il widget Feedaty mostra in anteprima ' +
        '(su ' + totalReviewsNum + ' totali del modello). Per sfogliare tutte le opinioni usa i tab dei singoli colori.';
      custom.appendChild(note);
    }

    var aggPageIndex = 0;
    var pageSize = MODAL_AGGREGATED_PAGE_SIZE;

    var pager = document.createElement('div');
    pager.className = 'fa-modal-pager';
    pager.setAttribute('role', 'navigation');
    pager.setAttribute('aria-label', 'Paginazione recensioni');

    var btnPrev = document.createElement('button');
    btnPrev.type = 'button';
    btnPrev.className = 'fa-modal-pager__btn';
    btnPrev.textContent = 'Precedente';

    var info = document.createElement('span');
    info.className = 'fa-modal-pager__info';

    var btnNext = document.createElement('button');
    btnNext.type = 'button';
    btnNext.className = 'fa-modal-pager__btn';
    btnNext.textContent = 'Successiva';

    pager.appendChild(btnPrev);
    pager.appendChild(info);
    pager.appendChild(btnNext);

    function totalAggPages() {
      if (allReviewClones.length === 0) return 1;
      return Math.ceil(allReviewClones.length / pageSize);
    }

    function renderAggregatedPage() {
      list.innerHTML = '';
      var tp = totalAggPages();
      aggPageIndex = Math.max(0, Math.min(aggPageIndex, tp - 1));
      var start = aggPageIndex * pageSize;
      var end = Math.min(start + pageSize, allReviewClones.length);
      for (var ri = start; ri < end; ri++) {
        list.appendChild(allReviewClones[ri].cloneNode(true));
      }
      if (allReviewClones.length <= pageSize) {
        pager.style.display = 'none';
      } else {
        pager.style.display = '';
        var shown = allReviewClones.length;
        var line2;
        if (totalReviewsKnown && shown > 0 && totalReviewsNum > shown) {
          line2 = shown + ' in anteprima su ' + totalReviewsNum + ' totali';
        } else {
          line2 = shown + (shown === 1 ? ' recensione' : ' recensioni');
        }
        info.textContent = 'Pagina ' + (aggPageIndex + 1) + ' di ' + tp + ' \u00b7 ' + line2;
        btnPrev.disabled = aggPageIndex <= 0;
        btnNext.disabled = aggPageIndex >= tp - 1;
      }
      try { custom.scrollTop = 0; } catch (e) {}
    }

    btnPrev.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (aggPageIndex <= 0) return;
      aggPageIndex--;
      renderAggregatedPage();
    });
    btnNext.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      if (aggPageIndex >= totalAggPages() - 1) return;
      aggPageIndex++;
      renderAggregatedPage();
    });

    custom.appendChild(list);
    custom.appendChild(pager);
    renderAggregatedPage();

    // Iframe: di default nascosto (siamo sul tab "Tutti").
    if (iframe) {
      iframe.setAttribute('hidden', 'hidden');
      iframe.setAttribute('aria-hidden', 'true');
    }

    // Tab switcher: gestione click delegata.
    function activate(target) {
      var btns = tabs.querySelectorAll('.fa-modal-tab');
      for (var i = 0; i < btns.length; i++) {
        if (String(btns[i].getAttribute('data-fa-target')) === String(target)) {
          btns[i].classList.add('is-active');
        } else {
          btns[i].classList.remove('is-active');
        }
      }
      if (target === 'all') {
        custom.removeAttribute('hidden');
        aggPageIndex = 0;
        renderAggregatedPage();
        if (iframe) {
          iframe.setAttribute('hidden', 'hidden');
          iframe.setAttribute('aria-hidden', 'true');
        }
      } else {
        // Tab di un sibling: costruisci URL e settalo sull'iframe.
        if (!iframe) return;
        var slug = String(target);
        var mapH = overlayUrlByHandle || {};
        var newSrc = null;
        var direct = mapH[slug];
        if (direct) {
          newSrc = absolutizeFeedatyUrl(direct);
          newSrc = normalizeFeedatyReviewsCanonicalUrl(newSrc);
          newSrc = ensureFeedatyOverlayQuery(newSrc);
          log('modal: iframe from template URL for handle', slug);
        }
        if (!newSrc) {
          var srcSource = iframe.getAttribute('src') || iframe.src || '';
          newSrc = buildSiblingIframeUrl(srcSource, slug);
          if (!newSrc && openOverlayBase) {
            newSrc = buildSiblingIframeUrl(openOverlayBase, slug);
          }
          if (newSrc) {
            newSrc = absolutizeFeedatyUrl(newSrc);
            newSrc = normalizeFeedatyReviewsCanonicalUrl(newSrc);
            newSrc = ensureFeedatyOverlayQuery(newSrc);
          }
        }
        if (!newSrc) {
          log(
            'modal: cannot resolve iframe url for handle',
            slug,
            'byHandleKeys=',
            Object.keys(mapH),
            'src=',
            iframe.getAttribute('src') || iframe.src
          );
          activate('all');
          return;
        }
        var refSrc = iframe.getAttribute('src') || iframe.src || '';
        newSrc = mergeFeedatyParamsFromReference(newSrc, refSrc);
        iframe.setAttribute('src', newSrc);
        iframe.removeAttribute('hidden');
        iframe.removeAttribute('aria-hidden');
        custom.setAttribute('hidden', 'hidden');
      }
    }

    // Handler cleanup-friendly: se ripopoliamo, sostituiamo i listener via cloning.
    var newTabs = tabs.cloneNode(true);
    tabs.parentNode.replaceChild(newTabs, tabs);
    tabs = newTabs;
    tabs.addEventListener('click', function (e) {
      var btn = e.target;
      while (btn && btn !== tabs && !btn.classList.contains('fa-modal-tab')) {
        btn = btn.parentNode;
      }
      if (!btn || btn === tabs) return;
      var target = btn.getAttribute('data-fa-target');
      if (!target) return;
      e.preventDefault();
      e.stopPropagation();
      activate(target);
    });

    // Reset al tab "Tutti" quando la modal viene (ri)aperta dall'utente.
    // Feedaty toggla la modal mostrandola via classe/style; ascoltiamo
    // entrambe le forme di mutazione. Qui facciamo anche il portale su body
    // (idempotente rispetto a portalProductModalsOnOpen grazie ai flag).
    if (!modal.__faModalOpenObserved) {
      observeModalOpen(modal, function () {
        if (!modal.__faOriginWidget) {
          modal.__faOriginWidget = modal.closest ? modal.closest('.feedaty_widget') : null;
        }
        if (modal.parentNode !== document.body) {
          try { document.body.appendChild(modal); } catch (e) {}
        }
        installPortaledModalCloseHandlers(modal);
        activate('all');
      });
      modal.__faModalOpenObserved = true;
    }

    return true;
  }

  /*
   * Osserva quando la modal Feedaty passa da "chiusa" a "aperta" e chiama cb.
   * Strategia: MutationObserver sugli attributi style e class della modal.
   * Consideriamo "aperta" se il computed display non e' "none". Per evitare
   * loop, teniamo un flag wasOpen e firamo solo sulla transizione false->true.
   */
  function observeModalOpen(modal, cb) {
    function isOpen() {
      try {
        var cs = window.getComputedStyle(modal);
        return cs && cs.display !== 'none' && cs.visibility !== 'hidden';
      } catch (e) {
        return false;
      }
    }
    var wasOpen = isOpen();
    var obs = new MutationObserver(function () {
      var open = isOpen();
      if (open && !wasOpen) {
        try { cb(); } catch (e) { log('modal open cb error', e); }
      }
      wasOpen = open;
    });
    try {
      obs.observe(modal, { attributes: true, attributeFilter: ['style', 'class'] });
    } catch (e) {
      log('observeModalOpen failed', e);
    }
  }

  /*
   * Feedaty (feedaty.min.js) attacca un unico listener "click" sul .feedaty_widget
   * che fa toggle display della modale. I click dentro il pannello risalgono al widget
   * e chiudono la modale. Intercettiamo i click sul box bianco .modal_popup_merch e
   * fermiamo la propagazione; la X (.feedaty_close_merch) e il backdrop (click sul
   * wrapper .feedaty_modal_merch fuori dal box) restano gestiti da Feedaty
   * (o dai nostri close handler quando la modale e' su body).
   */
  var feedatyModalShieldMoTimer = null;
  var feedatyModalShieldMo = null;

  function installFeedatyModalClickShield() {
    var popups = document.querySelectorAll('.feedaty_modal_merch .modal_popup_merch');
    for (var pi = 0; pi < popups.length; pi++) {
      var popup = popups[pi];
      if (popup.getAttribute('data-fa-popup-click-shield') === '1') continue;
      popup.setAttribute('data-fa-popup-click-shield', '1');
      popup.addEventListener(
        'click',
        function (ev) {
          if (ev.target && typeof ev.target.closest === 'function' && ev.target.closest('.feedaty_close_merch')) return;
          ev.stopPropagation();
        },
        false
      );
    }
    // Portale modali prodotto: gira su ready, sui retry e su ogni mutazione
    // del body, quindi becca anche modali create tardi da Feedaty.
    portalProductModalsOnOpen();
    installWidgetClickToModal();
  }

  function scheduleInstallFeedatyModalClickShield() {
    if (feedatyModalShieldMoTimer) return;
    feedatyModalShieldMoTimer = setTimeout(function () {
      feedatyModalShieldMoTimer = null;
      installFeedatyModalClickShield();
    }, 50);
  }

  function ensureFeedatyModalClickShieldObserver() {
    if (feedatyModalShieldMo || !document.body) return;
    try {
      feedatyModalShieldMo = new MutationObserver(function () {
        scheduleInstallFeedatyModalClickShield();
      });
      feedatyModalShieldMo.observe(document.body, { childList: true, subtree: true });
    } catch (err) {
      log('modal click shield observer failed', err);
    }
  }

  function bootFeedatyModalClickShield() {
    whenFeedatyReady(function () {
      installFeedatyModalClickShield();
      setTimeout(installFeedatyModalClickShield, 0);
      setTimeout(installFeedatyModalClickShield, 400);
      ensureFeedatyModalClickShieldObserver();
    });
  }

  function setupCustomModal(reviewInput, rating, count, siblings, currentHandle, openOverlayBase, overlayUrlByProductId, overlayUrlByHandle) {
    var items = normalizeModalReviewInput(reviewInput);
    if (!items.length) return false;
    function isModalInsideProductFeedatyWidget(modalEl) {
      // __faOriginWidget come fallback: dopo il portale su body closest()
      // non trova piu' il widget antenato.
      var w = (modalEl && modalEl.__faOriginWidget) ||
              (modalEl && modalEl.closest ? modalEl.closest('.feedaty_widget') : null);
      if (!w) return false;
      var t = (w.getAttribute('data-type') || '').toLowerCase();
      return t === 'carouselproduct' || t === 'product';
    }
    // querySelectorAll perche gli id "feedaty_modal_merch" sono duplicati:
    // c'e' una modal per ogni widget Feedaty in pagina (rating sintetico,
    // carosello, ecc). Il widget JS apre la modal del widget cliccato, quindi
    // dobbiamo popolarle TUTTE quelle PRODOTTO (non merchant: altrimenti iframe
    // nativo senza le nostre tab).
    var modals = document.querySelectorAll('.feedaty_modal_merch, [id="feedaty_modal_merch"]');
    if (!modals.length) { log('modal: no .feedaty_modal_merch in page, skipping'); return false; }
    ensureModalStyles();
    var done = 0;
    for (var i = 0; i < modals.length; i++) {
      if (!isModalInsideProductFeedatyWidget(modals[i])) continue;
      if (populateOneModal(modals[i], reviewInput, rating, count, siblings, currentHandle, openOverlayBase, overlayUrlByProductId, overlayUrlByHandle)) done++;
    }
    log(
      'modal: populated',
      done,
      '/',
      modals.length,
      'modals, review items=',
      items.length,
      'overlay by id=',
      overlayUrlByProductId ? Object.keys(overlayUrlByProductId).length : 0,
      'by handle=',
      overlayUrlByHandle ? Object.keys(overlayUrlByHandle).length : 0
    );
    return done > 0;
  }

  function revealFeedatyIntroWhenPopulated() {
    var intros = document.querySelectorAll('.feedaty_intro');
    if (!intros.length) return;

    function widgetForIntro(intro) {
      var scope = (intro.closest && (intro.closest('.feedaty_wrapper') || intro.closest('.shopify-section'))) || document;
      var candidates = scope.querySelectorAll('.feedaty_widget');
      for (var i = 0; i < candidates.length; i++) {
        if (isProductWidget(candidates[i])) return candidates[i];
      }
      return null;
    }

    whenFeedatyReady(function () {
      var pending = Array.prototype.slice.call(intros);
      var attempts = 0;
      var iv = setInterval(function () {
        attempts++;
        for (var i = pending.length - 1; i >= 0; i--) {
          var intro = pending[i];
          var w = widgetForIntro(intro);
          if (w && widgetHasVisibleContent(w)) {
            intro.style.display = '';
            pending.splice(i, 1);
            if (DEBUG) log('intro rivelata (widget popolato)');
          }
        }
        if (!pending.length || attempts >= 20) {
          clearInterval(iv);
          if (DEBUG && pending.length) {
            log('intro: widget vuoto dopo', attempts, 'tentativi, resta nascosta (', pending.length, ')');
          }
        }
      }, POLL_INTERVAL_MS);
    });
  }

  function start() {
    ensureAggregatorCustomCssLink();
    var data = readSiblingsData();
    if (!data) { log('no siblings data, aborting'); return; }
    var others = data.siblingIds.filter(function (id) { return id && id !== data.currentId; });
    if (others.length === 0) {       
      // log message if no other siblings
      log('no other siblings, nothing to aggregate'); 
      return; 
    }
    log('siblings to aggregate (excluding current):', others);
    applyFeedatyWidgetGateIfAggregating(data);

    var productById = buildProductByIdMap(data.siblings);

    whenFeedatyReady(function () {
      ensureAggregatorCustomCssLink();
      var widgets = findVisibleProductWidgets();
      if (!widgets.length) {
        log('no product widgets in page, aborting');
        clearFeedatyWidgetGate();
        return;
      }
      log('product widgets in page:', widgets.length);
      beginFeedatyWidgetGateReadyObserver();

      var debugBucket = [];
      var globalState = {
        modalReviewItems: null,
        modalRating: null,
        modalCount: null,
        openOverlayBase: null,
        overlayUrlByProductId: {},
        overlayUrlByHandle: {}
      };
      var jobs = widgets.map(function (w) {
        return processWidget(w, others, debugBucket, globalState, productById).catch(function (e) {
          log('processWidget failed', e);
          debugBucket.push({
            dataId: w.getAttribute('data-id'),
            sku: w.getAttribute('data-sku'),
            error: String(e && e.message || e)
          });
        });
      });

      Promise.all(jobs)
        .then(function () {
        var byHandle = {};
        (data.siblings || []).forEach(function (s) {
          if (!s || !s.handle) return;
          var u = globalState.overlayUrlByProductId && globalState.overlayUrlByProductId[String(s.id)];
          if (u) byHandle[s.handle] = u;
        });
        globalState.overlayUrlByHandle = byHandle;

        var modalOk = false;
        if (globalState.modalReviewItems && globalState.modalReviewItems.length) {
          try {
            shuffleArrayInPlace(globalState.modalReviewItems);
            if (DEBUG) log('modal: review order shuffled (', globalState.modalReviewItems.length, 'items)');
            modalOk = setupCustomModal(
              globalState.modalReviewItems,
              globalState.modalRating,
              globalState.modalCount,
              data.siblings || null,
              data.currentHandle || '',
              globalState.openOverlayBase || null,
              globalState.overlayUrlByProductId || {},
              globalState.overlayUrlByHandle || {}
            );
          }
          catch (e) { log('setupCustomModal failed', e); }
        }
        try {
          window.__feedatyAggregator = {
            siblings: data,
            captures: getCaptures().map(function (c) { return { url: c.url, at: c.at, keys: c.json ? Object.keys(c.json) : [] }; }),
            widgets: debugBucket,
            overlayUrlByProductId: globalState.overlayUrlByProductId || {},
            overlayUrlByHandle: globalState.overlayUrlByHandle || {},
            modal: {
              populated: modalOk,
              reviewsCount: globalState.modalReviewItems ? globalState.modalReviewItems.length : 0,
              rating: globalState.modalRating,
              count: globalState.modalCount,
              openOverlayBase: globalState.openOverlayBase || null,
              overlayProductIds: Object.keys(globalState.overlayUrlByProductId || {}),
              overlayHandles: Object.keys(globalState.overlayUrlByHandle || {})
            }
          };
        } catch (e) {}
        if (DEBUG) {
          try {
            console.groupCollapsed('[feedaty-aggregator] debug dump');
            console.log('siblings data:', data);
            console.log('captures (url + json keys):', window.__feedatyAggregator && window.__feedatyAggregator.captures);
            console.log('widgets:', debugBucket);
            console.log('modal:', window.__feedatyAggregator && window.__feedatyAggregator.modal);
            console.log('Tip: window.__feedatyAggregator contiene tutto il debug.');
            console.groupEnd();
          } catch (e) {}
        }
        try {
          scheduleFeedatyAggregatorReadyMarker();
        } catch (eReady) {}
      })
      .catch(function (err) {
        log('aggregate chain error', err);
        try {
          scheduleFeedatyAggregatorReadyMarker();
        } catch (eReady) {}
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
  bootFeedatyModalClickShield();
  revealFeedatyIntroWhenPopulated();

  document.addEventListener('FeedatyLoaded', function () {
    ensureAggregatorCustomCssLink();
  });
  window.addEventListener('load', function () {
    ensureAggregatorCustomCssLink();
  });
})();